import {
    Component,
    Material,
    math,
    MeshComponent,
    Object3D,
    PhysXComponent,
} from '@wonderlandengine/api';
import {property} from '@wonderlandengine/api/decorators.js';
import {DropMode, PuzzleCamera} from './puzzle-camera.js';
import {Puzzle, PuzzleConstants} from './puzzle.js';
import {quat, vec2, vec3} from 'gl-matrix';
import {lerpVec3, Easing} from '../utils/lerp.js';
import {CustomLogTag} from '../classes/Constants.js';
import {AxisX, AxisY, Mathf, Vec3One, Vec3Zero} from '../utils/Mathf.js';
import {Mathfx} from '../utils/mathFx.js';
import {WLUtils} from '../utils/WLUtils.js';
import {SpecialPower} from './special-power.js';
import {AnimationController} from '@wonderlandengine/tweenjs';
import {BoardAnimations} from './animations/BoardAnimations.js';
import {RNG} from '../utils/rng.js';
import {Sounds} from '../enums/Sounds.js';
import {AudioDirector} from './audio-director.js';
import {Statistics} from './statistics.js';
import {StatNames} from '../enums/StatNames.js';
import {Tags} from './tags.js';
import {BoardObjectTags} from '../enums/BoardObjectTags.js';
import {TimeManager} from './time-manager.js';
import {Trail} from './trail.js';
import {Coroutine} from '../classes/coroutines/Coroutine.js';
import {CoroutineManager} from '../classes/coroutines/CoroutineManager.js';
import {waitForSeconds} from '../classes/coroutines/YieldInstructions.js';
import {VEC3_ONE} from '../utils/Constants.js';
import {FXPrefabs} from './fx-prefabs.js';

enum Mode {
    Drop,
    Placed,
    FallBack,
    Dead,
    Paused,
    HopOnMal,
    CrushMal,
    None,
    FreezeDrop,
}

export type BounceUpData = {
    copyOfMal: Object3D;
    //Shader blobShader;
    //UnityEngine.Color transparency;
    scaleAdjustment: number;
    heightAdjustment: number;
};

/**
 * The color of the board object.
 * @remark the odd order is an exact copy of the original Unity project
 */
export enum BoardColor {
    Orange = 0,
    Green,
    Blue,
    Yellow,
    None,
    Any,
    Red,
    Purple,
    Pink,
    Moon,
    Brown,
    Turquoise,
}

const transformPositionVec = vec3.create();
const transformPositionVec2 = vec3.create();
const transformLaneVec = vec3.create();
const tempQuat = quat.create();

export class BoardObject extends Component {
    static TypeName = 'board-object';

    @property.float(2)
    slideSpeed = 2.0;

    @property.float(1.5)
    dropSpeed = 1.5;

    @property.float(60)
    dropImmediateSpeed = 60;

    @property.float(30)
    fallBackAcceleration = 30;

    @property.float(-5)
    initialFallBackSpeed = -5;

    @property.float(1)
    leftRightSpeed = 1;

    @property.float(0.5)
    clickIntoPlaceDelay = 0.5;

    @property.int(4)
    clickIntoPlaceMaxLandings = 4;

    @property.bool(true)
    isFruit = true;

    @property.float(1500)
    dropRotationSpeed = 1500;

    @property.float(90)
    dropEaseStartAngle = 90;

    @property.enum(Object.keys(BoardColor).filter((b) => isNaN(Number(b))))
    color: BoardColor;

    @property.color()
    colorTint = [0, 0, 0, 0];

    @property.int(1)
    minBlinkDelay = 1;

    @property.int(5)
    maxBlinkDelay = 5;

    /**
     * True if the object can be loaded with another object.
     */
    @property.bool(true)
    canBeLoaded = true;

    /**
     * The material to use when the object is loaded, or loaded byu another object.
     */
    @property.material()
    loadedMaterial?: Material;

    @property.bool(false)
    inheritColorOnlyWhenAbsorbed = false;

    @property.object()
    animatorObject!: Object3D;

    @property.object()
    loadedParent!: Object3D;

    @property.object()
    loadedView!: Object3D;

    @property.object()
    unloadedView!: Object3D;

    @property.bool(false)
    clickingRequiresLoading = false;

    @property.bool(false)
    explodeRainbow = false;

    // Note: These will likely change since they will probably be in a different file
    // and thus will be references by name (= string) instead of by object.
    // @property.object()
    // freezeDropFX!: Object3D;

    // @property.object()
    // freezeDropShatterFX!: Object3D;

    @property.float(0.4)
    deleteForPopAnimDelay = 0.4;

    @property.float(0.4)
    deleteAfterExplodeDelay = 0.4;

    @property.object()
    fruitTrail: Object3D = null;

    /**** Animations ****/

    @property.string('')
    idleAnimation = '';

    @property.string('')
    loadedAnimation = '';

    @property.string('')
    chainAnimation = '';

    @property.string('')
    blinkAnimation = '';
    @property.string('')
    fullBlinkAnimation = '';
    @property.string('')
    popAnimation = '';
    @property.string('')
    pressAnimation = '';
    @property.string('')
    absorbAnimation = '';
    @property.string('')
    squashAnimation = '';
    @property.string('')
    dropAnimation = '';
    @property.string('')
    landAnimation = '';
    @property.string('')
    getAbsorbedAnimation = '';
    @property.string('')
    hopOnMalAnimation = '';
    @property.string('')
    bouceBackWithMalAnimation = '';
    @property.string('')
    loadHiccupAnimation = '';
    @property.string('')
    spitOutAnimation = '';
    @property.string('')
    spitOutEmptyAnimation = '';
    @property.string('')
    crushMalAnimation = '';
    @property.string('')
    eyesClosedIdleAnimation = '';

    /**** Audio ****/
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    popSounds: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    popMaxSounds: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    absorbSounds: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    exertSounds: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    spitOutSounds: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    hiccupSounds: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    fruitFallFastSfx: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    fruitLandSfx: Sounds = Sounds.none;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    fruitLandFastSfx: Sounds = Sounds.none;
    @property.int(15)
    fruitSpeedSfxTolerance = 15;
    @property.enum(Object.keys(Sounds).filter((e) => isNaN(Number(e))), Sounds.none)
    splashSfx: Sounds = Sounds.none;

    @property.float(-2)
    bouceBackAfterHopOffset = -2;

    @property.string('')
    explodeFX = '';

    private _loaded: any;
    private _bounceAfterHopDelay: number;
    private _hopTargetRotation: quat;
    get loaded() {
        return this._loaded;
    }

    camera: PuzzleCamera;

    /**
     * The puzzle this object is part of.
     *
     * @remarks In unity the public propety was called `PuzzleController`, with a private
     * backing field of `puzzle`, I assume this was needed to prevent a property in the Unity Editor.
     * To make it easier to use similar code I used a public property `puzzle` without a backing field.
     */
    puzzle: Puzzle;

    row: number;
    column: number;
    fallBackDelay: number = 0;

    private _mode = Mode.Paused;

    private _targetPosition: vec3 = vec3.create();
    private _startPosition: vec3 = vec3.create();
    private _step: number = 0.0;
    private _dropImmediateTriggered: boolean;
    private _dropRow: number;
    private _dropColumn: number;
    private _puzzleCamera: PuzzleCamera;
    private _currentFallSpeed: number = 1;
    private _time = 0;
    private _isFallingStackBottom: boolean = false;
    private _fallBackTimer: number = 0;

    private _canBeEjected: boolean;
    private _loadedObject: Object3D = null;
    private _dropRadius: number;
    private _degreesPerColumn: number;
    private _dropAngle: number;
    private _dropAngleTarget: number;
    private _speed: number;
    private _targetColumn: number;
    private _placeTimer: number;
    private _numberOfLandings: number;
    private _gameResetRegistered: any;
    private _rotationCorrectionStartTime: number;
    private _loadedCausesBloat: boolean;
    private _wasClicked: boolean;
    private _popIndex: number;
    private _pendingExplosionTime: number;
    private _originalMaterial: Material;
    private _animator: AnimationController;
    private _idleAnimationName: string;
    private _currentBlinkDelay: number;
    private _fruitTrailRef: Trail | null = null;
    private _addedTrail: boolean = false;
    private _playedFastLandSfx: boolean = true;
    private _coroutineManager: CoroutineManager = new CoroutineManager();

    init() {}

    start() {
        this._step = -1.0;
        if (this.loadedView && this.unloadedView) {
            WLUtils.setActive(this.loadedView, false);
            WLUtils.setActive(this.unloadedView, true);
        }
        const meshComp = this.loadedParent?.getComponent(MeshComponent);
        if (meshComp) {
            this._originalMaterial = this.loadedParent.getComponent(MeshComponent).material;
        } else {
            this.engine.log.error(
                CustomLogTag.Game,
                `BoardObject ${this.object.name} has no MeshComponent`
            );
        }

        this._currentBlinkDelay =
            this._time + RNG.getUniform(this.minBlinkDelay, this.maxBlinkDelay);

        if (this.animatorObject) {
            this._animator = this.animatorObject.getComponent(AnimationController);
        }
        if (this._animator) {
            if (this.idleAnimation) {
                this._idleAnimationName = 'idle';
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.idleAnimation),
                    'idle'
                );
                this._animator.play('idle');
            } else {
                // TODO: The original creates an empty animation clip.
                // not sure how to handle that yet.
            }

            if (this.loadedAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.loadedAnimation),
                    'loaded'
                );
            }
            if (this.absorbAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.absorbAnimation),
                    'absorb'
                );
            }
            if (this.getAbsorbedAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.getAbsorbedAnimation),
                    'getAbsorbed'
                );
            }
            if (this.blinkAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.blinkAnimation),
                    'blink'
                );
                this._animator.setLayer('blink', 1);
            }
            if (this.fullBlinkAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.fullBlinkAnimation),
                    'fullBlink'
                );
                this._animator.setLayer('fullBlink', 1);
            }
            if (this.popAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.popAnimation),
                    'pop'
                );
            }
            if (this.pressAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.pressAnimation),
                    'press'
                );
            }
            if (this.dropAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.dropAnimation),
                    'drop'
                );
            }
            if (this.landAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.landAnimation),
                    'land'
                );
            }
            if (this.hopOnMalAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.hopOnMalAnimation),
                    'hop'
                );
            }
            if (this.bouceBackWithMalAnimation) {
                this._animator.addClip(
                    BoardAnimations.instance.getAnimation(this.bouceBackWithMalAnimation),
                    'bounceBack'
                );
            }
        }
    }

    update(dt: number) {
        if (TimeManager.instance.paused) {
            // bit of a hacky way to pause the game
            return;
        }
        this._coroutineManager.update(dt);
        this._time += dt;
        switch (this._mode) {
            case Mode.Drop:
                this._updateDrop(dt);
                break;
            case Mode.Placed:
                this._updatePlaced(dt);
                break;

            case Mode.FallBack:
                this._updateFall(dt);
                break;

            case Mode.Dead:
                this._updateDead(dt);
                break;
            case Mode.Paused:
                this._updatePause();
                break;
            case Mode.HopOnMal:
                this._updateHopOnMal();
                break;

            case Mode.FreezeDrop:
                this._updateFreezeDrop();
                break;
        }
    }

    landWhenDestroyed() {
        if (this._mode == Mode.FallBack) {
            this.puzzle.objectLanded();
        }
    }

    moveToPosition(target: vec3) {
        vec3.copy(this._targetPosition, target);
        if (this._step < 0.0) {
            this._step = 0.0;
            this.object.getPositionLocal(this._startPosition);
        }
    }

    place() {
        this._mode = Mode.Placed;
        this._step = -1;

        const power = this._getSpecialPowers();
        if (power && power.length > 0) {
            for (let x = 0; x < power.length; x++) {
                power[x].placed(this.puzzle, this);
            }
        }

        if (this._fruitTrailRef) {
            setTimeout(() => {
                if (this._fruitTrailRef && !this._fruitTrailRef.isDestroyed) {
                    this._fruitTrailRef.destroy();
                    this._fruitTrailRef = null;
                }
            }, 200);
        }

        this._unregisterResetCallback();
        const height = this.puzzle.getRowOffset(this.row);
        this.object.getPositionLocal(transformPositionVec);
        transformPositionVec[1] = height;
        this.object.setPositionLocal(transformPositionVec);
    }

    pause() {
        this._mode = Mode.Paused;
        if (this._animator && this.getAbsorbedAnimation) {
            this._animator.play('getAbsorbed');
            this._animator.playQueued(this._idleAnimationName);
        }

        this._registerResetCallback();
    }

    drop(puzzle: Puzzle, targetRow: number, targetColumn: number) {
        this._dropImmediateTriggered = false;
        this._mode = Mode.Drop;
        this.puzzle = puzzle;
        this._dropRow = targetRow;
        this._dropColumn = targetColumn;
        this._puzzleCamera = puzzle.mainCamera;

        puzzle.lanes[0].getPositionWorld(transformPositionVec);
        this._dropRadius = vec2.len(
            vec2.fromValues(transformPositionVec[0], transformPositionVec[2])
        );
        this._degreesPerColumn = 360.0 / puzzle.lanes.length;
        this._dropAngle = this._degreesPerColumn * targetColumn;
        this._dropAngleTarget = this._dropAngle;

        this.object.getPositionWorld(transformPositionVec);
        puzzle.lanes[this._dropColumn].getPositionWorld(transformLaneVec);
        puzzle.lanes[this._dropColumn].getRotationWorld(tempQuat);

        this.object.setPositionWorld([
            transformLaneVec[0],
            transformPositionVec[1],
            transformLaneVec[2],
        ]);
        this.object.setRotationWorld(tempQuat);

        if (this._animator && this.dropAnimation) {
            this._animator.play('drop');
            this._animator.playQueued(this._idleAnimationName);
        }

        this._numberOfLandings = 0;
        this._registerResetCallback();
    }

    fallBack(newRow: number, isStackBottom: boolean): boolean {
        const currentlyStationary = this._mode == Mode.Placed;

        if (currentlyStationary) {
            this._mode = Mode.FallBack;
            this._currentFallSpeed = this.initialFallBackSpeed;
            this._isFallingStackBottom = isStackBottom;
            this._fallBackTimer = this._time;
        }

        this.row = newRow;

        return currentlyStationary;
    }

    canBeAbsorbed(): boolean {
        return this.isFruit && this._mode != Mode.FallBack;
    }

    canCombineWith(other: BoardObject): boolean {
        let canCombine = false;
        if (
            other &&
            this.canBeLoaded &&
            (!this._loaded || this._canBeEjected) &&
            other.isFruit
        ) {
            canCombine = this.matchesMyColor(other, false);
        }

        return canCombine;
    }

    matchesMyColor(other: BoardObject, exactMatch: boolean): boolean {
        let match = false;
        if (other) {
            if (exactMatch) {
                match =
                    this.color == other.color &&
                    this.color != BoardColor.None &&
                    other.color != BoardColor.None;
            } else {
                match =
                    (this.color == other.color ||
                        this.color == BoardColor.Any ||
                        other.color == BoardColor.Any) &&
                    this.color != BoardColor.None &&
                    other.color != BoardColor.None;
            }
        }
        return match;
    }

    combineWith(obj: BoardObject) {
        if (!this.canBeLoaded) {
            this.engine.log.error(
                CustomLogTag.Game,
                `Object cannot combine with anything (${this.object.name} trying to absorb ${obj.object.name})`
            );
        } else if (this._loaded || this._loadedObject != null) {
            if (this._canBeEjected) {
                this.explodeLoadedObject();
                this.loadObject(obj.object);
            } else {
                this.engine.log.error(
                    CustomLogTag.Game,
                    `Can't load an object that's already loaded! (${this.object.name} trying to absorb ${obj.object.name})`
                );
            }
        } else {
            this.loadObject(obj.object);
        }
    }

    loadObject(objToLoad: Object3D) {
        if (this._loaded && this._loadedObject == objToLoad) {
            // loaded already!
            return;
        }
        const boardObjToLoad = objToLoad.getComponent(BoardObject);
        let inheritColorOnly = false;
        let explodeRainbow = false;
        if (boardObjToLoad) {
            inheritColorOnly = boardObjToLoad.inheritColorOnlyWhenAbsorbed;
            explodeRainbow =
                boardObjToLoad.isFruit && boardObjToLoad.color == BoardColor.Any;
            boardObjToLoad.absorbed(this);
            boardObjToLoad.destroy(); // get rid of the BoardObject but keep the gameObject.
        }

        this._loadedObject = objToLoad;

        // RenderOrder[] renderOrders = obj.GetComponentsInChildren<RenderOrder>();
        // for (int x = 0; x < renderOrders.Length; x++)
        // {
        //     renderOrders[x].SetupChildObject(this, true);
        // }

        this._loaded = true;

        // // we inherit the tag from our child
        const newTag = Tags.getFirst(this._loadedObject);
        // // ... and assume it
        Tags.setTag(this._loadedObject, 'Untagged');
        Tags.setTag(this.object, newTag);

        this._canBeEjected = false;

        this._loadedCausesBloat = true;
        // if (QualityControlManager.GetInstance().Quality != QualityControlManager.DeviceQuality.VeryLow)
        // {
        //     if (eyeLeft)
        //     {
        //         eyeLeft.enabled = true;
        //     }
        //     if (eyeRight)
        //     {
        //         eyeRight.enabled = true;
        //     }
        // }
        // else if (lowQualityEyes != null)
        // {
        //     Utils.SetActiveRecursively(lowQualityEyes, true);
        // }

        //array for Quickplay mode...
        const power = WLUtils.getComponentsOfType(this._loadedObject, SpecialPower);

        if (power.length > 0) {
            for (let x = 0; x < power.length; x++) {
                this._loadedCausesBloat = power[0].bloatParent;
                this._canBeEjected = power[0].canBeEjected;
                //         if (power[0].HideEyes)
                //         {
                //             if (eyeLeft)
                //             {
                //                 eyeLeft.enabled = false;
                //             }
                //             if (eyeRight)
                //             {
                //                 eyeRight.enabled = false;
                //             }
                //             if (lowQualityEyes)
                //             {
                //                 Utils.SetActiveRecursively(lowQualityEyes, false);
                //             }
                //         }
                //         else if (power[0].CloseEyes && eyesClosedIdleAnimation)
                //         {
                //             idleAnimationName = "eyesClosed";
                //             animator.Play(idleAnimationName);
                //         }

                //         if (power[0].AttachToBaseFX)
                //         {
                //             Vector3 localPosition = power[0].AttachToBaseFX.transform.localPosition;
                //             Quaternion localRotation = power[0].AttachToBaseFX.transform.localRotation;
                //             Vector3 localScale = power[0].AttachToBaseFX.transform.localScale;
                //             power[0].AttachToBaseFX.transform.parent = loadedParent.transform.parent;
                //             power[0].AttachToBaseFX.transform.localPosition = localPosition;
                //             power[0].AttachToBaseFX.transform.localRotation = localRotation;
                //             power[0].AttachToBaseFX.transform.localScale = localScale;
                //         }

                power[x].loaded(this);
            }
        }

        if (this._loadedCausesBloat && this._idleAnimationName == 'idle') {
            this._idleAnimationName = 'loaded';
        }

        if (this.loadedView && this.unloadedView) {
            if (this._loadedCausesBloat) {
                this.loadedParent = this.loadedView;
                WLUtils.setActive(this.loadedView, true);
                WLUtils.setActive(this.unloadedView, false);
            } else {
                this.loadedParent = this.unloadedView;
                WLUtils.setActive(this.loadedView, false);
                WLUtils.setActive(this.unloadedView, true);
            }
        }

        this._loadedObject.parent = this.loadedParent;
        this._loadedObject.setPositionLocal(Vec3Zero);
        this._loadedObject.setScalingLocal(Vec3One);
        if (boardObjToLoad && boardObjToLoad.isFruit) {
            this._loadedObject.rotateAxisAngleDegLocal(AxisX, 90);
            this._loadedObject.translateLocal([0, 0.5, 0]);
        }
        this.loadedParent.getComponent(MeshComponent).material = this.loadedMaterial;

        //if (!inheritColorOnly) {
        // boardObjToLoad.loadedParent.getComponent(MeshComponent).material =
        //     boardObjToLoad.loadedMaterial;
        //     MeshRenderer[] renderers = loadedObject.GetComponentsInChildren<MeshRenderer>();
        //     for (int x = 0; x < renderers.Length; x++)
        //     {
        //         renderers[x].material.shader = loadedObjectShader;
        //         UnityEngine.Color newColor = loadedObjectColorTint;
        //         renderers[x].material.color = newColor;
        ///}
        //} else {
        if (inheritColorOnly) {
            // let m = boardObjToLoad.loadedMaterial.clone() as FlatMaterial;
            // m.color = this.colorTint;
            //    boardObjToLoad.loadedParent.getComponent(MeshComponent).material = m;
            //     MeshRenderer[] renderers = loadedObject.GetComponentsInChildren<MeshRenderer>();
            //     for (int x = 0; x < renderers.Length; x++)
            //     {
            //         if (renderers[x].material.shader == loadedObjectShader)
            //         {
            //             UnityEngine.Color newColor = loadedObjectColorTint;
            //             renderers[x].material.color = newColor;
            //         }
            //     }
        }

        if (this._loadedCausesBloat) {
            if (this._animator && this.loadedAnimation) {
                if (this.absorbAnimation) {
                    this._animator.play('absorb');
                    this._animator.playQueued('loaded');
                } else {
                    this._animator.play('loaded');
                }
            }
            // Should this only play if there's an animation?
            if (this.absorbSounds != Sounds.none) {
                AudioDirector.instance.playOneShot(this.absorbSounds, true);
            }
        }
    }

    absorbed(newParent: BoardObject) {
        if (this._animator) {
            //animator.transform.rotation = Quaternion.identity;
            if (this.getAbsorbedAnimation) {
                this._animator.play('getAbsorbed');
            } else {
                this._animator.stop();
                // animator.transform.localScale = Vector3.one;
                // animator.transform.localRotation = Quaternion.identity;
                // animator.transform.localPosition = Vector3.zero;
            }
        }
        this.object.resetScaling();
        newParent.object.getRotationLocal(tempQuat);
        this.object.setRotationLocal(tempQuat);
    }

    explodeLoadedObject() {
        if (this._loadedObject) {
            const obj = this._unloadObject();
            // // spit out SFX
            // if (spitOutSounds != null && spitOutSounds.Length > 0)
            // {
            //     int index = Random.Range(0, spitOutSounds.Length);
            //     soundDirector.PlayOneShot(spitOutSounds[index], true);
            // }
            const power = WLUtils.getComponentsOfType(obj, SpecialPower);
            if (power.length > 0) {
                for (let x = 0; x < power.length; x++) {
                    power[x].explode(this.puzzle, this.row, this.column);
                }
            } else {
                obj.destroy();
            }

            // make sure we're tagged correctly.
            this._setTagToColor();
        }
    }

    public press() {
        if (this.pressAnimation && this._animator) {
            this._animator.play('press');
            this._animator.playQueued(this._idleAnimationName);
        }
    }
    private _setTagToColor() {
        switch (this.color) {
            case BoardColor.Blue:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceBlue]);
                break;
            case BoardColor.Green:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceGreen]);
                break;
            case BoardColor.Orange:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceOrange]);
                break;
            case BoardColor.Yellow:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceYellow]);
                break;
            case BoardColor.Red:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceRed]);
                break;
            case BoardColor.Purple:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PiecePurple]);
                break;
            case BoardColor.Pink:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PiecePink]);
                break;
            case BoardColor.Moon:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceMoon]);
                break;
            case BoardColor.Brown:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceBrown]);
                break;
            case BoardColor.Turquoise:
                Tags.setTag(this.object, BoardObjectTags[BoardObjectTags.PieceTurquoise]);
                break;
        }
    }

    private _unloadObject(): Object3D {
        const obj = this._loadedObject;
        if (obj) {
            this._loaded = false;
            obj.parent = null;
            this._loadedObject = null;

            let spitEmpty = false;
            const power = WLUtils.getComponentOfType(obj, SpecialPower);
            if (power) {
                spitEmpty = !power.bloatParent;
                // if (power.AttachToBaseFX)
                // {
                // 	Logger.Log("Attach to base FX is: " + power.AttachToBaseFX.name);
                // 	Destroy(power.AttachToBaseFX);
                // }
            }

            // RenderOrder[] renderOrders = obj.GetComponentsInChildren<RenderOrder>();
            // for (int x = 0; x < renderOrders.Length; x++)
            // {
            // 	// it's not going to be on the board after this, so let's
            // 	// stop RenderOrder for now.
            // 	renderOrders[x].enabled = false;
            // }

            if (this.loadedView && this.unloadedView) {
                WLUtils.setActive(this.loadedView, false);
                WLUtils.setActive(this.unloadedView, true);
            }

            // if (smallBevel)
            // {
            // 	Utils.SetActiveRecursively(smallBevel, false);
            // }

            // if (animator && idleAnimation)
            // {
            // 	idleAnimationName = "idle";
            // 	if (spitOutAnimation && spitOutEmptyAnimation)
            // 	{
            // 		if (spitEmpty)
            // 		{
            // 			animator.Play("spitOutEmpty");
            // 		}
            // 		else
            // 		{
            // 			animator.Play("spitOut");
            // 		}
            // 		animator.PlayQueued(idleAnimationName);
            // 	}
            // 	else
            // 	{
            // 		animator.Play(idleAnimationName);
            // 	}
            // }

            // note that this doesn't fix the shader!
        }
        return obj;
    }

    private _updateDrop(dt: number) {
        //     if (puzzle.Rules.GetSpecialMode(SpecialMode.FreezeDrop))
        //     {
        //         //juice up drop speed to make it get in position faster
        //         speed = dropImmediateSpeed / 4;
        //     }
        //     else
        //     {
        //         // determine drop speed (increase)
        this._speed = this.dropSpeed * this.puzzle.dropSpeedFactor;
        //     }

        if (
            this._dropImmediateTriggered ||
            this._puzzleCamera.currentDropMode == DropMode.Immediate
        ) {
            // || puzzle.Rules.IsCoconutEmergency())
            if (!this._dropImmediateTriggered) {
                AudioDirector.instance.playOneShot(this.fruitFallFastSfx, true);
            }

            //         if (puzzle.Rules.IsCoconutEmergency())
            //         {
            //             speed = Mathf.Max(dropImmediateSpeed * puzzle.Rules.coconutEmergencySpeedFactor, speed);
            //         }
            //         else
            //         {
            this._speed = this.dropImmediateSpeed;
            //         }
            this._dropImmediateTriggered = true;

            //         //destroy freeze drop fx
            //         if (spawnedFreezeDropFX != null)
            //         {
            //             Quaternion rot = puzzle.Lanes[targetColumn].transform.rotation;
            //             float angle;
            //             Vector3 axis;
            //             rot.ToAngleAxis(out angle, out axis);
            //             angle += 180.0f;	// turn it around
            //             rot = Quaternion.AngleAxis(angle, axis);

            //             Destroy(spawnedFreezeDropFX);
            //             Instantiate(freezeDropShatterFX, transform.position, rot);

            //             //play SFX
            //             if (freezeCrashSFX && playedFreezeCrash == false )
            //             {
            //                 playedFreezeCrash = true;
            //                 soundDirector.PlayOneShot(freezeCrashSFX, true);
            //             }

            //         }
        }

        if (this.fruitTrail && this._addedTrail == false) {
            this._addedTrail = true;
            const trailObject = this.fruitTrail.clone(this.object);
            this._fruitTrailRef = trailObject.getComponent(Trail);
            this._fruitTrailRef.setMaterialColor(this.color);
            this._fruitTrailRef.active = true;
        }
        this.object.getPositionWorld(transformPositionVec);
        transformPositionVec[1] -= this._speed * dt;
        this.object.setPositionWorld(transformPositionVec);

        // update row first
        this.puzzle.lanes[0].getPositionWorld(transformLaneVec);
        let newRow = this.puzzle.offsetToRow(transformPositionVec[1] - transformLaneVec[1]);

        //     if (row == -1 && newRow != -1 && puzzle.Rules.GetSpecialMode(SpecialMode.FreezeDrop) && waitedForDrop == false)
        //     {
        //         FreezeDrop();
        //     }
        //     else
        //     {
        this._dropRow = newRow;

        if (this._dropRow > -1) {
            let columnTop = this.puzzle.getHighestOccupiedRow(this._dropColumn);
            if (columnTop >= this._dropRow) {
                // hack!
                this._dropRow = columnTop + 1;
            }
        }

        this._targetColumn = this._puzzleCamera.findClosestLane();
        const laneCount = this.puzzle.lanes.length;
        if (this._targetColumn != this._dropColumn) {
            const direction = this._getClosestDirection(
                this._dropColumn,
                this._targetColumn
            );
            let safeTarget = this._dropColumn;
            let laneIndex = this._dropColumn;
            while (laneIndex != this._targetColumn) {
                laneIndex = ~~Mathf.repeat(laneIndex + direction, laneCount);
                if (
                    this._dropRow > -1 &&
                    this.puzzle.getHighestOccupiedRow(laneIndex) >= this._dropRow
                ) {
                    // we can't pass this column;
                    break;
                } else {
                    safeTarget = laneIndex;
                }
            }
            if (safeTarget != this._dropColumn) {
                this._dropAngleTarget = this._degreesPerColumn * safeTarget;
                this._dropColumn = safeTarget;
                // if (animator && dropAnimation && !animator.IsPlaying("drop"))
                // {
                //     animator.Play("drop");
                // }
                this._placeTimer = -1.0;
            }
        }

        if (!Mathf.approximately(this._dropAngle, this._dropAngleTarget)) {
            const forwardAngleDelta = Mathfx.directionalAngleDelta(
                this._dropAngle,
                this._dropAngleTarget,
                true
            );
            const reverseAngleDelta = Mathfx.directionalAngleDelta(
                this._dropAngle,
                this._dropAngleTarget,
                false
            );
            const angleDelta = Math.min(forwardAngleDelta, reverseAngleDelta);
            let speedMultiplier = 1.0;
            if (angleDelta < this.dropEaseStartAngle) {
                speedMultiplier = angleDelta / this.dropEaseStartAngle;
            }
            this._dropAngle = Mathfx.moveTowardsAngleDirection(
                this._dropAngle,
                this._dropAngleTarget,
                speedMultiplier * this.dropRotationSpeed * dt,
                forwardAngleDelta < reverseAngleDelta
            );
            const angleRadians = this._dropAngle * Mathf.Deg2Rad;
            transformPositionVec[0] = Math.sin(angleRadians) * this._dropRadius;
            //tempVec[1]=transform.position.y,
            transformPositionVec[2] = Math.cos(angleRadians) * this._dropRadius;
            this.object.setPositionWorld(transformPositionVec);
            //transform.position = columnPosition;
            quat.setAxisAngle(
                tempQuat,
                AxisY,
                (PuzzleConstants.localColumnRotation + this._dropAngle) * Mathf.Deg2Rad
            );
            this.object.setRotationWorld(tempQuat);
        }
        // look at placement on the board
        // irrelevant if we're not on the board yet.
        if (this._dropRow > -1) {
            // set limits for the camera
            const nextColumn = (this._dropColumn + 1) % laneCount;
            const previousColumn =
                this._dropColumn == 0 ? laneCount - 1 : this._dropColumn - 1;
            let maxColumn = -1;
            let minColumn = -1;
            if (this.puzzle.getHighestOccupiedRow(nextColumn) >= this._dropColumn) {
                // can't go to the next column
                maxColumn = this._dropColumn;
            }
            if (this.puzzle.getHighestOccupiedRow(previousColumn) >= this._dropRow) {
                // can't go to the previous column
                minColumn = this._dropColumn;
            }
            this._puzzleCamera.setMaxLane(maxColumn);
            this._puzzleCamera.setMinLane(minColumn);
            this._puzzleCamera.angleClamp();

            if (this.puzzle.getHighestOccupiedRow(this._dropColumn) == this._dropRow - 1) {
                //   stop when we land on this row
                this.puzzle.lanes[this._dropColumn].getPositionLocal(transformLaneVec);
                this.object.getPositionLocal(transformPositionVec);
                const targetHeight =
                    this.puzzle.getRowOffset(this._dropRow) + transformLaneVec[1];
                if (transformPositionVec[1] <= targetHeight || this._placeTimer > 0.0) {
                    transformPositionVec[1] = targetHeight;
                    this.object.setPositionLocal(transformPositionVec);

                    let clickImmediate = this._dropImmediateTriggered;
                    let belowMe: BoardObject = null;
                    if (this._dropRow > 0) {
                        belowMe = this.puzzle.getObject(
                            this._dropRow - 1,
                            this._dropColumn
                        );
                        if (
                            belowMe &&
                            belowMe.canCombineWith(this) &&
                            this.matchesMyColor(belowMe, true)
                        ) {
                            clickImmediate = true;
                        }
                    }
                    if (this._placeTimer > 0.0 || clickImmediate) {
                        this._placeTimer -= dt;
                        if (
                            this._numberOfLandings > this.clickIntoPlaceMaxLandings ||
                            this._placeTimer <= 0.0 ||
                            clickImmediate
                        ) {
                            this.row = this._dropRow;
                            this.column = this._dropColumn;
                            this.puzzle.addObject(this, this.row, this.column);
                            this.place();
                            this._puzzleCamera.setMaxLane(-1);
                            this._puzzleCamera.setMinLane(-1);
                            this._puzzleCamera.angleClamp();
                            this.puzzle.turnAdvance();
                            // puzzleCamera.ResetSwipe();
                            this._rotationCorrectionStartTime = this._time;
                            if (clickImmediate) {
                                if (this.landAnimation && this._animator) {
                                    //      Vector3 rotation = animator.transform.localEulerAngles;
                                    this._animator.play('land');
                                    this._animator.playQueued(this._idleAnimationName);
                                    //      if (correctRotationOnLand)
                                    //      {
                                    //          Vector3 parentRot = animator.transform.parent.transform.localEulerAngles;
                                    //          parentRot.y = rotation.y;
                                    //          animator.transform.parent.transform.localRotation = Quaternion.Euler(parentRot);
                                    //          rotationCorrectionDegrees = parentRot.y;
                                    //      }
                                    this._playLandSfx();
                                }
                                if (belowMe && !belowMe.canCombineWith(this)) {
                                    belowMe.squash();
                                } else if (this.row == 0) {
                                    //      Instantiate(splashFX, transform.position - new Vector3(0.0f, 0.3f, 0.0f), Quaternion.identity);
                                    if (this.splashSfx != Sounds.none) {
                                        AudioDirector.instance.playOneShot(
                                            this.splashSfx,
                                            true
                                        );
                                    }
                                }
                            }
                        }
                    } else {
                        // We landed on the top of this column for the first time.
                        this._placeTimer = this.clickIntoPlaceDelay;
                        this._numberOfLandings++;
                        if (belowMe) {
                            belowMe.squash();
                        } else if (this._dropRow == 0) {
                            // Instantiate(splashFX, transform.position - new Vector3(0.0f, 0.3f, 0.0f), Quaternion.identity);
                            if (this.splashSfx != Sounds.none) {
                                AudioDirector.instance.playOneShot(this.splashSfx, true);
                            }
                        }
                        if (this.landAnimation && this._animator) {
                            //     Vector3 rotation = animator.transform.localEulerAngles;
                            this._animator.play('land');
                            this._animator.playQueued(this._idleAnimationName);
                            //     if (correctRotationOnLand)
                            //     {
                            //         Vector3 parentRot = animator.transform.parent.transform.localEulerAngles;
                            //         parentRot.y = rotation.y;
                            //         animator.transform.parent.transform.localRotation = Quaternion.Euler(parentRot);
                            //         rotationCorrectionDegrees = parentRot.y;
                            //     }
                            this._playLandSfx();
                        }
                    }
                } else {
                    // We're in the air.
                    this._placeTimer = -1.0;
                }
            }
        }
    }

    squash() {
        if (this._animator && this.squashAnimation) {
            this._animator.play('squash');
            this._animator.playQueued(this._idleAnimationName);
        }
    }

    click() {
        if (
            this._mode == Mode.Placed &&
            !this.isFruit &&
            (!this.clickingRequiresLoading || this._loaded)
        ) {
            this._wasClicked = true;

            this.puzzle.registerForActivation(this.row, this.column);

            if (this._loaded) {
                const power = WLUtils.getComponentOfType(this._loadedObject, SpecialPower);
                if (power && power.bloatParent) {
                    this.puzzle.poppedBlob(this.color);
                }
            }
        }
    }

    activateSpecial() {
        const power = this._getSpecialPowers();
        if (power.length > 0) {
            for (let x = 0; x < power.length; x++) {
                power[x].activate(this.puzzle, this.row, this.column);
            }
        }
    }

    pop(delay: number, chainStep: number, isRainbow: boolean) {
        if (this._mode != Mode.Dead) {
            this.explodeRainbow ||= isRainbow;

            const power = this._getSpecialPowers();
            if (power.length > 0) {
                for (let x = 0; x < power.length; x++) {
                    power[x].popped(this.puzzle, this.row, this.column);
                }
            }

            this._popIndex = chainStep;

            if (this._mode == Mode.FallBack) {
                // don't wait for us to land.
                this.puzzle.objectLanded();
            }

            this._bounceStack(delay);

            this._die();

            if (delay > 0) {
                this._pendingExplosionTime = delay;
            } else {
                this._explode();
            }
        }
    }

    hopOnMal(
        bounce: BounceUpData,
        delay: number,
        bounceUpDelay: number,
        targetRotation: quat,
        newScale: number,
        destroyDelay: number
    ) {
        this._mode = Mode.HopOnMal;
        this._bounceAfterHopDelay = bounceUpDelay;
        this._hopTargetRotation = targetRotation;
        this._coroutineManager.stopCoroutine(this._doHopId);
        this._doHopId = this._coroutineManager.addCoroutine(
            new Coroutine(this._doHop(bounce, delay, newScale, destroyDelay))
        );
    }
    private _doHopId = -1;
    private *_doHop(
        bounce: BounceUpData,
        delay: number,
        newScale: number,
        destroyDelay: number
    ) {
        if (delay > 0) {
            yield* waitForSeconds(delay);
        }
        // 	RenderOrder[] renderOrders = GetComponentsInChildren<RenderOrder>();
        // 	for (int x = 0; x < renderOrders.Length; x++)
        // 	{
        // 		// HACK.  Force all renderOrders to think the blob is visible without
        // 		// turning them off.
        // 		renderOrders[x].visibilityThreshold = 2.0f;
        // 	}
        // 	// prevent loading from firing normal animation
        // 	loadedAnimation = null;
        // 	absorbAnimation = null;

        if (this.hopOnMalAnimation) {
            this._animator.play('hop');

            this._coroutineManager.stopCoroutine(this._doBounceId);
            this._coroutineManager.addCoroutine(
                new Coroutine(
                    this._doBounceUp(
                        bounce,
                        this._animator.getLength('hop') + this._bounceAfterHopDelay,
                        newScale
                    )
                )
            );

            if (!bounce) {
                this._coroutineManager.stopCoroutine(this._doUnloadObjectId);
                this._doUnloadObjectId = this._coroutineManager.addCoroutine(
                    new Coroutine(
                        this._doUnloadObjectRoutine(
                            this._animator.getLength('hop') +
                                destroyDelay +
                                this._bounceAfterHopDelay
                        )
                    )
                );
            }
        }
    }

    private _doBounceId = -1;
    private *_doBounceUp(bounce: BounceUpData, delay: number, newScale: number) {
        yield* waitForSeconds(delay);
        if (bounce != null && this.bouceBackWithMalAnimation) {
            transformPositionVec[0] = 0.0;
            transformPositionVec[2] = 0.0;
            transformPositionVec[1] = 12 + this.bouceBackAfterHopOffset * newScale;
            this.object.setPositionWorld(transformPositionVec);
            this.object.setRotationWorld(this._hopTargetRotation);
            this.object.rotateAxisAngleDegObject(AxisY, 180);

            // 	if (animator.IsPlaying("eyesClosed"))
            // 	{
            // 		animator.Stop("eyesClosed");
            // 		animator.Play("idle");
            // 	}
            // 	RenderOrder[] blobRenderOrders = gameObject.GetComponentsInChildren<RenderOrder>(true);
            // 	for (int i = 0; i < blobRenderOrders.Length; i++)
            // 	{
            // 		blobRenderOrders[i].enabled = false;
            // 		Destroy(blobRenderOrders[i]);
            // 	}
            // 	if (loadedObject)
            // 	{
            // 		RenderOrder[] loadedRenderOrders = loadedObject.GetComponentsInChildren<RenderOrder>(true);
            // 		for (int i = 0; i < loadedRenderOrders.Length; i++)
            // 		{
            // 			loadedRenderOrders[i].enabled = false;
            // 			Destroy(loadedRenderOrders[i]);
            // 		}
            // 	}

            this._doUnloadObject();
            this.loadedParent.getComponent(MeshComponent).material = this.loadedMaterial;
            WLUtils.setActive(this.loadedView, true);
            WLUtils.setActive(this.unloadedView, false);

            // 	materialsToDestroy = new List<Material>();
            // 	MeshRenderer[] blobRenderers = gameObject.GetComponentsInChildren<MeshRenderer>(true);
            // 	for (int i = 0; i < blobRenderers.Length; i++)
            // 	{
            // 		Material mat = Instantiate(blobRenderers[i].sharedMaterial) as Material;
            // 		materialsToDestroy.Add(mat);
            // 		blobRenderers[i].material = mat;
            // 		blobRenderers[i].material.shader = bounce.blobShader;
            // 		if (blobRenderers[i].gameObject == loadedView.gameObject)
            // 		{
            // 			blobRenderers[i].material.color = bounce.transparency;
            // 		}
            // 		else
            // 		{
            // 			blobRenderers[i].material.color = UnityEngine.Color.white;
            // 		}
            // 	}
            // bounce.copyOfMal.setPositionWorld(transformPositionVec);
            // bounce.copyOfMal.resetRotation(); //transform.rotation = Quaternion.identity;
            WLUtils.setActive(bounce.copyOfMal, true);
            // 	if (QualityControlManager.GetInstance().Quality != QualityControlManager.DeviceQuality.VeryLow)
            // 	{
            // 		if (eyeLeft)
            // 		{
            // 			eyeLeft.enabled = true;
            // 		}
            // 		if (eyeRight)
            // 		{
            // 			eyeRight.enabled = true;
            // 		}
            // 	}
            // 	else if (lowQualityEyes != null)
            // 	{
            // 		Utils.SetActiveRecursively(lowQualityEyes, true);
            // 	}
            this.object.setScalingLocal([newScale, newScale, newScale]);

            this._loadedObject = bounce.copyOfMal;

            this._loaded = true;
            this._canBeEjected = false;
            this._loadedCausesBloat = true;
            this.loadedParent = this.loadedView;
            //this._loadedObject.parent = this.object;
            this._loadedObject.parent = this.loadedParent;
            this._loadedObject.resetPosition();
            this._loadedObject.setScalingWorld(VEC3_ONE);
            this._loadedObject.translateWorld([0, -0.5, 0]);

            //this._loadedObject.translateLocal([0, 1, 0]);

            //this._loadedObject.setPositionWorld(transformPositionVec);
            // this._loadedObject.setPositionWorld([
            //     0,
            //     12, //-(bounce.heightAdjustment * newScale),
            //     0,
            // ]);

            this._loadedObject.setRotationWorld(this._hopTargetRotation);
            //this._loadedObject.resetPosition();
            // 	loadedObject.transform.parent = loadedParent;

            // this.object.rotateAxisAngleDegLocal(AxisY, 180);
            // 	loadedObject.transform.localScale = Vector3.one / (newScale * bounce.scaleAdjustment);
            // 	loadedObject.transform.forward = -transform.forward;	//mal is backwards. ugh.
            // 	Vector3 localPos = new Vector3(0, 0, bounce.heightAdjustment / newScale);
            //    this._loadedObject.setPositionWorld(transformPositionVec);
            this._animator.play('bounceBack');
            // 	// Should this only play if there's an animation?
            // 	if (absorbSounds != null && absorbSounds.Length > 0)
            // 	{
            // 		int index = Random.Range(0, absorbSounds.Length);
            AudioDirector.instance.playOneShot(this.absorbSounds, true);
            // 	}
            // 	// copyOfMal will be destroyed when unloaded.
        }
        // // prevent the animation from changing after this
        // animator = null;
    }

    private _doUnloadObjectId = -1;
    private *_doUnloadObjectRoutine(delay: number) {
        yield* waitForSeconds(delay);
        this._doUnloadObject();
    }

    private _doUnloadObject() {
        const obj = this._unloadObject();
        if (obj) {
            obj.destroy();
        }
    }

    clickActivated() {
        this._wasClicked = true;
    }

    private *_explodeFX(fx: string) {
        if (this.object.isDestroyed) {
            return;
        }
        if (this.puzzle) {
            yield* waitForSeconds(0.15);
            // 	// rotate to face out from the lane
            // 	Quaternion rot = puzzle.Lanes[column].transform.rotation;
            const ang = this.column * (360 / PuzzleConstants.columnCount) + 180;
            // 	float angle;
            // 	Vector3 axis;
            // 	rot.ToAngleAxis(out angle, out axis);
            // 	angle += 180.0f;	// turn it around
            // 	rot = Quaternion.AngleAxis(angle, axis);
            // 	if (QualityControlManager.GetInstance().Quality != QualityControlManager.DeviceQuality.VeryLow)
            // 	{
            // 		ObjectPoolManager.GetEffectPool().PoolInstantiate(fx, transform.position, rot);
            // 	}
            const fxObject = FXPrefabs.instance.spawn(fx, null, false);
            fxObject.setPositionWorld(this.object.getPositionWorld([]));
            fxObject.rotateAxisAngleDegObject(AxisY, ang);
            fxObject.translateObject([0, 0, -1]);
            WLUtils.setActive(fxObject, true);
        }
        yield* waitForSeconds(0.1);

        this.object.destroy();

        // ObjectPoolManager.GetBlobPool().PoolDestroy(gameObject, 0.1f);
    }

    private _explode() {
        // it's possible for this function to be called after the game has ended via the pause
        // menu.  In this case the game object is deactivated but hasn't been destroyed yet.
        // Handle that case.
        //  if (!this.object.active) {
        //TK not sure how this should be handled in WL (activeInHierarchy)
        //         ObjectPoolManager.GetBlobPool().PoolDestroy(gameObject);
        //  } else {
        const power = this._getSpecialPowers();
        const explosionEffect = this.explodeFX;

        if (power.length > 0) {
            for (let x = 0; x < power.length; x++) {
                //             GameObject fx = power.GetBlobPopFX(color);
                //             if (fx != null)
                //             {
                //                 explosionEffect = fx;
                //             }
                //             else if (explodeRainbow && rainbowExplodeFX)
                //             {
                //                 explosionEffect = rainbowExplodeFX;
                //             }
                power[x].explode(this.puzzle, this.row, this.column);
            }
        }
        //  else if (this.explodeRainbow && this.rainbowExplodeFX)
        //         {
        //             explosionEffect = rainbowExplodeFX;
        //  }
        //Turn them glowy before they explode
        if (this.loadedView && this.unloadedView) {
            WLUtils.setActive(this.loadedView, true);
            WLUtils.setActive(this.unloadedView, false);
        }

        if (this._animator && this.popAnimation) {
            this._animator.play('pop');
        }

        if (this._wasClicked == true) {
            // TODO: should not hardcode; why isn't animation length working?
            // TK: Todo came from original game?
            this.deleteAfterExplodeDelay = this.deleteForPopAnimDelay;
        }

        if (explosionEffect) {
            this._coroutineManager.addCoroutine(
                new Coroutine(this._explodeFX(explosionEffect))
            );
            //             StartCoroutine("ExplodeFX", explosionEffect);
        }

        if (!this.isFruit) {
            Statistics.instance.recordAdditive(StatNames.BlobsPopped, 1);
            switch (this.color) {
                case BoardColor.Orange:
                    Statistics.instance.recordAdditive(StatNames.OrangePopped, 1);
                    break;
                case BoardColor.Green:
                    Statistics.instance.recordAdditive(StatNames.GreenPopped, 1);
                    break;
                case BoardColor.Blue:
                    Statistics.instance.recordAdditive(StatNames.BluePopped, 1);
                    break;
                case BoardColor.Red:
                    Statistics.instance.recordAdditive(StatNames.RedPopped, 1);
                    break;
                case BoardColor.Yellow:
                    Statistics.instance.recordAdditive(StatNames.YellowPopped, 1);
                    break;
                case BoardColor.Pink:
                    Statistics.instance.recordAdditive(StatNames.PinkPopped, 1);
                    break;
                case BoardColor.Purple:
                    Statistics.instance.recordAdditive(StatNames.PurplePopped, 1);
                    break;
                case BoardColor.Moon:
                    Statistics.instance.recordAdditive(StatNames.MoonPopped, 1);
                    break;
                case BoardColor.Brown:
                    Statistics.instance.recordAdditive(StatNames.BrownPopped, 1);
                    break;
                case BoardColor.Turquoise:
                    Statistics.instance.recordAdditive(StatNames.TurquoisePopped, 1);
                    break;
                default:
                    break;
            }
        } else if (this.isFruit) {
            Statistics.instance.recordAdditive(StatNames.FruitPopped, 1);
            // pumpkin
            if (this.color == BoardColor.Brown) {
                Statistics.instance.recordAdditive(StatNames.FruitBrownPopped, 1);
            }
        }

        if (this.exertSounds != Sounds.none && this._wasClicked == true) {
            AudioDirector.instance.playOneShot(this.exertSounds, true);
        }

        if (this._popIndex >= 0 && this.popSounds != Sounds.none && !this._wasClicked) {
            //                 int index = popIndex;

            //                 // At max, play random pop max sound
            //                 if (index > popSounds.Length - 1)
            //                 {
            //                     if (popMaxSounds != null  && popMaxSounds.Length > 0)
            //                     {
            //                         soundDirector.PlayOneShot(popMaxSounds[index % popMaxSounds.Length], true);
            //                     }
            //                 }
            //                 else
            //                 {
            AudioDirector.instance.playOneShot(this.popSounds, true);
            //                 }
            //             }
        }

        // TK: Right now there's no pool implemented. Just destroying the objects for now
        if (!explosionEffect) {
            //ObjectPoolManager.GetBlobPool().PoolDestroy(gameObject, deleteAfterExplodeDelay);
            //}
            WLUtils.destroyWithDelay(this.object, this.deleteAfterExplodeDelay * 1000);
        }
    }

    private _die() {
        this._mode = Mode.Dead;

        this._registerResetCallback();
    }
    private _bounceStack(delay: number) {
        if (this.puzzle && this.row >= 0) {
            for (let x = this.row + 1; x < PuzzleConstants.rowCount; x++) {
                const aboveMe = this.puzzle.getObject(x, this.column);
                if (aboveMe) {
                    aboveMe.fallBackDelay = delay;
                } else {
                    break;
                }
            }
        }
    }

    private _getSpecialPowers(): SpecialPower[] {
        {
            let power = [];

            if (this._loadedObject) {
                power = WLUtils.getComponentsOfType(this._loadedObject, SpecialPower);
            }

            if (power.length === 0) {
                // do we have our own power?
                power = WLUtils.getComponentsOfType(this.object, SpecialPower);
            }

            return power;
        }
    }

    private _getClosestDirection(fromColumn: number, toColumn: number) {
        const firstClamped = ~~Mathf.repeat(fromColumn, this.puzzle.lanes.length);
        const secondClamped = ~~Mathf.repeat(toColumn, this.puzzle.lanes.length);

        let forwardDistance = secondClamped - firstClamped;
        let reverseDistance = firstClamped + this.puzzle.lanes.length - secondClamped;

        if (secondClamped < firstClamped) {
            reverseDistance = firstClamped - secondClamped;
            forwardDistance = secondClamped + this.puzzle.lanes.length - firstClamped;
        }

        if (forwardDistance < reverseDistance) {
            return 1;
        } else {
            return -1;
        }
    }

    private _updatePlaced(dt: number) {
        if (this._step > -1.0) {
            this._step;
            this._step += dt * this.slideSpeed;
            const newPosition: vec3 = vec3.create();
            if (this._step >= 1.0) {
                vec3.copy(newPosition, this._targetPosition);
                this._step = -1.0;
            } else {
                lerpVec3(
                    newPosition,
                    this._startPosition,
                    this._targetPosition,
                    this._step,
                    Easing.Hermite
                );
            }
            this.object.setPositionLocal(newPosition);
        }

        if (
            this._animator &&
            this.blinkAnimation &&
            this.fullBlinkAnimation &&
            this._time > this._currentBlinkDelay
        ) {
            this._doBlink();
            this._currentBlinkDelay =
                this._time + RNG.getUniform(this.minBlinkDelay, this.maxBlinkDelay);
        }
    }
    private _updatePause() {}
    private _updateFreezeDrop() {}
    private _updateHopOnMal() {}

    private _updateDead(dt: number) {
        if (this._pendingExplosionTime > 0.0) {
            this._pendingExplosionTime -= dt;
            if (this._pendingExplosionTime <= 0.0) {
                this._explode();
            }
        }
    }

    private _updateFall(dt: number) {
        if (
            this._fallBackTimer < 0.0 ||
            this._fallBackTimer + this.fallBackDelay < this._time
        ) {
            this._fallBackTimer = -1.0;
            this.fallBackDelay = 0.0;
            this._currentFallSpeed += this.fallBackAcceleration * dt;
            this.object.getPositionWorld(transformPositionVec);
            transformPositionVec[1] -= this._currentFallSpeed * dt;
            this.object.setPositionWorld(transformPositionVec);
            // stop when we land on this row
            const targetHeight = this.puzzle.getRowOffset(this.row);

            if (transformPositionVec[1] <= targetHeight) {
                // check to see if there's anything under us now
                const belowMe: BoardObject =
                    this.row == 0 ? null : this.puzzle.getObject(this.row - 1, this.column);
                transformPositionVec[1] = targetHeight;
                this.object.setPositionWorld(transformPositionVec);
                this.place();
                this.puzzle.objectLanded();
                if (this._animator && this.squashAnimation) {
                    // && !this._animator.IsPlaying("squash")) //TK: Needs to be impemented in animation lib
                    this._animator;
                    this._animator.play('squash');
                    this._animator.playQueued(this._idleAnimationName);
                }
                if (this.row > 0 && this._isFallingStackBottom) {
                    if (!belowMe.canCombineWith(this)) {
                        belowMe.squash();
                    }
                } else if (this.row == 0) {
                    // Instantiate(splashFX, transform.position - new Vector3(0.0f, 0.3f, 0.0f), Quaternion.identity);
                    if (this.splashSfx != Sounds.none) {
                        AudioDirector.instance.playOneShot(this.splashSfx, true);
                    }
                }
            }
        }
    }

    private _registerResetCallback() {
        if (!this._gameResetRegistered) {
            this._gameResetRegistered = true;
            Puzzle.instance.registerResetGameCallback(this._resetGame);
        }
    }

    private _unregisterResetCallback() {
        if (this._gameResetRegistered) {
            this._gameResetRegistered = false;
            Puzzle.instance.unregisterResetGameCallback(this._resetGame);
        }
    }
    private _resetGame = (puzzleController: Puzzle) => {
        if (this._gameResetRegistered) {
            this._unregisterResetCallback();
            this.object.destroy();
        }
    };

    private _doBlink() {
        if (this._idleAnimationName != 'eyesClosed') {
            if (this._loaded && this._loadedCausesBloat && this.fullBlinkAnimation) {
                this._animator.play('fullBlink');
            } else if (this.blinkAnimation) {
                this._animator.play('blink');
            }
        }
    }

    private _playLandSfx() {
        if (this._speed >= this.fruitSpeedSfxTolerance && this._playedFastLandSfx == true) {
            this._playedFastLandSfx = false;

            // play large sfx
            if (this.fruitLandFastSfx != Sounds.none) {
                AudioDirector.instance.playOneShot(this.fruitLandFastSfx, true);
            }
        } else {
            // play sfx
            if (this.fruitLandSfx != Sounds.none) {
                AudioDirector.instance.playOneShot(this.fruitLandSfx, true);
            }
        }
    }

    static boardColorToChar(color: BoardColor): ColorChar {
        switch (color) {
            case BoardColor.Orange:
                return 'o';
            case BoardColor.Green:
                return 'g';
            case BoardColor.Blue:
                return 'b';
            case BoardColor.Yellow:
                return 'y';
            case BoardColor.None:
                return '0';
            case BoardColor.Any:
                return '*';
            case BoardColor.Red:
                return 'r';
            case BoardColor.Purple:
                return 'l';
            case BoardColor.Pink:
                return 'p';
            case BoardColor.Brown:
                return 'n';
            case BoardColor.Moon:
                return 'm';
            case BoardColor.Turquoise:
                return 't';
        }
    }

    static charToBoardColor(color: ColorChar): BoardColor {
        switch (color.toLowerCase()) {
            case 'o':
                return BoardColor.Orange;
            case 'g':
                return BoardColor.Green;
            case 'b':
                return BoardColor.Blue;
            case 'y':
                return BoardColor.Yellow;
            case '*':
                return BoardColor.Any;
            case 'r':
                return BoardColor.Red;
            case 'l':
                return BoardColor.Purple;
            case 'p':
                return BoardColor.Pink;
            case 'n':
                return BoardColor.Brown;
            case 'm':
                return BoardColor.Moon;
            case 't':
                return BoardColor.Turquoise;
            case '0':
                return BoardColor.None;
        }
    }
}

export type ColorChar =
    | 'o'
    | 'g'
    | 'b'
    | 'y'
    | '0'
    | '*'
    | 'r'
    | 'l'
    | 'p'
    | 'n'
    | 'm'
    | 't';
